import { extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import React, { useContext, useState, useMemo, useEffect } from 'react';
import { useTranslate } from '@awardit/react-use-translate';
import { useSendMessage } from 'crustate/react';
import { setAddresses } from '@crossroads/shop-state/quote';
import { Foldable, Checkbox } from '@crossroads/ui-components';
import { ContactInfo, Address } from '../Address/index.js';
import { Button } from '../Button/index.js';
import { CheckboxField } from '../Field/index.js';
import { focusInvalidField } from '../../helpers/utils.js';
import { rules, isEmail, isRequired, nestedRule, isPhone, conditional, Form } from '@awardit/formaggio';
import styles from 'shop-views/CustomerForm.scss';

var emailRegEx = /^[^\s@]+@example\.com$/;

var hasExampleEmail = email => emailRegEx.test(email);

var isNotExampleEmail = (f, msg) => t => typeof t[f] === "string" && !emailRegEx.test(t[f]) ? [] : [{
  error: msg != null ? msg : "EMAIL",
  field: f
}];

var validationName = () => rules([isRequired("firstname"), isRequired("lastname")]);

var validationPhone = () => rules([isPhone("telephone")]);

var validationAddress = () => rules([isRequired("postcode"), isRequired("city"), nestedRule("street", rules([isRequired("0")])), isRequired("countryCode")]);

var validation = rules([isEmail("email"), isNotExampleEmail("email"), isRequired("billing"), nestedRule("billing", validationName()), nestedRule("billing", validationPhone()), conditional(s => Boolean(!s.onlyVirtual), rules([nestedRule("billing", validationAddress())])), conditional(s => Boolean(!s.shipToSameAddress), rules([isRequired("shipping"), nestedRule("shipping", validationName()), nestedRule("shipping", validationAddress())]))]);
var CustomerForm = _ref => {
  var _quoteData$data;

  var isAddressValid = _ref.isAddressValid,
      setAddressIsValid = _ref.setAddressIsValid,
      quoteData = _ref.quoteData,
      StoreInfoContext = _ref.StoreInfoContext;
  var t = useTranslate();
  var sendMessage = useSendMessage();
  var quote = quoteData ? quoteData.data : null;
  var addresses = (quote == null ? void 0 : quote.addresses) || [];
  var quoteBilling = addresses.find(x => x.type === "billing");
  var quoteShipping = addresses.find(x => x.type === "shipping");

  var _useContext = useContext(StoreInfoContext),
      _useContext$info = _useContext.info,
      countries = _useContext$info.countries,
      customerInputFieldsSettings = _useContext$info.customerInputFieldsSettings;

  var _useState = useState(false),
      showErrors = _useState[0],
      setShowErrors = _useState[1];

  var validQuoteBillingCountry = useMemo(() => {
    return countries.find(c => c.code === (quoteBilling == null ? void 0 : quoteBilling.country.code));
  }, [countries, quoteBilling]);
  var validQuoteShippingCountry = useMemo(() => {
    return countries.find(c => c.code === (quoteShipping == null ? void 0 : quoteShipping.country.code));
  }, [countries, quoteShipping]);
  var isVirtual = quoteData == null ? void 0 : (_quoteData$data = quoteData.data) == null ? void 0 : _quoteData$data.items.every(x => x.product.type === "virtual" || x.configOption && x.configOption.product.type === "virtual"); // eslint-disable-next-line complexity

  var _useState2 = useState(() => {
    if (!validQuoteBillingCountry) {
      return {
        email: quote != null && quote.email && !hasExampleEmail(quote.email) ? quote.email : "",
        telephone: quoteBilling != null && quoteBilling.telephone ? quoteBilling.telephone : "",
        billing: _extends({
          firstname: quoteBilling != null && quoteBilling.firstname ? quoteBilling.firstname : "",
          lastname: quoteBilling != null && quoteBilling.lastname ? quoteBilling.lastname : "",
          countryCode: (validQuoteBillingCountry == null ? void 0 : validQuoteBillingCountry.code) || "",
          countryName: "",
          telephone: quoteBilling != null && quoteBilling.telephone ? quoteBilling.telephone : "",
          postcode: quoteBilling != null && quoteBilling.postcode ? quoteBilling.postcode : "",
          city: quoteBilling != null && quoteBilling.city ? quoteBilling.city : "",
          street: quoteBilling != null && quoteBilling.street ? quoteBilling.street : [""]
        }, isVirtual && {
          postcode: "-",
          city: "-",
          street: ["-"]
        }),
        shipping: {
          firstname: "",
          lastname: "",
          countryCode: "",
          countryName: "",
          postcode: "",
          city: "",
          street: [""],
          telephone: ""
        },
        shipToSameAddress: true,
        onlyVirtual: isVirtual,
        checkDiscountCode: ""
      };
    }

    return {
      email: quote != null && quote.email && !hasExampleEmail(quote.email) ? quote.email : "",
      telephone: quoteBilling != null && quoteBilling.telephone ? quoteBilling.telephone : "",
      billing: _extends({}, quoteBilling, isVirtual && {
        postcode: "-",
        city: "-",
        street: ["-"]
      }, {
        countryCode: validQuoteBillingCountry.code || "",
        countryName: validQuoteBillingCountry.name
      }),
      shipping: _extends({}, quoteShipping, {
        countryCode: (validQuoteShippingCountry == null ? void 0 : validQuoteShippingCountry.code) || validQuoteBillingCountry.code || "",
        countryName: countries.length === 1 ? validQuoteBillingCountry.name : validQuoteShippingCountry == null ? void 0 : validQuoteShippingCountry.name
      }),
      shipToSameAddress: (quoteBilling == null ? void 0 : quoteBilling.type) === "billing" ? quoteBilling.isUsedAsShipping : false,
      onlyVirtual: isVirtual,
      checkDiscountCode: ""
    };
  }),
      state = _useState2[0],
      setState = _useState2[1];

  var billing = state.billing,
      shipping = state.shipping;
  var errors = validation(state);
  useEffect(() => {
    if (quoteData.state === "LOADED" && errors.length === 0) {
      setAddressIsValid(true);
    }
  }, []);

  var submit = e => {
    e.preventDefault();

    if (countries.length > 1) {
      var _countries$find, _countries$find2;

      setState(_extends({}, state, {
        billing: _extends({}, state.billing, {
          countryName: (_countries$find = countries.find(c => c.code === state.billing.countryCode)) == null ? void 0 : _countries$find.name
        }),
        shipping: _extends({}, state.shipping, {
          countryName: (_countries$find2 = countries.find(c => c.code === state.shipping.countryCode)) == null ? void 0 : _countries$find2.name
        })
      }));
    }

    var billing = {
      firstname: state.billing.firstname,
      lastname: state.billing.lastname,
      street: state.billing.street,
      postcode: state.billing.postcode,
      city: state.billing.city,
      countryCode: state.billing.countryCode,
      telephone: state.billing.telephone
    };
    var shipping = {
      firstname: state.shipping.firstname,
      lastname: state.shipping.lastname,
      street: state.shipping.street,
      postcode: state.shipping.postcode,
      city: state.shipping.city,
      countryCode: state.shipping.countryCode,
      telephone: state.shipping.telephone
    };
    sendMessage(setAddresses(state.email, billing, shipping, state.shipToSameAddress));
    setAddressIsValid(true);

    if (showErrors === true) {
      setShowErrors(false);
    }
  };

  var handleErrors = (e, errors) => {
    var fieldSettingsMap = customerInputFieldsSettings.reduce((acc, item) => {
      acc[item.field] = item.setting;
      return acc;
    }, {});
    var hiddenErrorFields = errors.map(item => {
      // This is to replace "bad" names comming from error
      // so they can match what comes from StoreInfo.
      // eslint-disable-next-line quote-props
      var replacements = {
        "0": "address"
      };
      var field = replacements[item.field.split(".").pop()] || item.field.split(".").pop();
      return fieldSettingsMap[field] === "hidden" ? field : null;
    }).filter(Boolean);

    if (hiddenErrorFields.length > 0) {
      setShowErrors(true);
    }

    focusInvalidField(e, errors);
  };

  return /*#__PURE__*/React.createElement(Form, {
    autoComplete: "on",
    value: state,
    errors: errors,
    onError: handleErrors,
    onChange: x => {
      setState(_extends({}, state, x));
    },
    onSubmit: submit
  }, /*#__PURE__*/React.createElement(Foldable, {
    open: isAddressValid
  }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("h3", {
    className: styles.customerFormHeading
  }, billing.firstname, " ", billing.lastname), !isVirtual && /*#__PURE__*/React.createElement("p", null, Object.values(billing.street).join(", "), /*#__PURE__*/React.createElement("br", null), billing.postcode, " ", billing.city, " ", billing.countryName), /*#__PURE__*/React.createElement("p", null, state.email, /*#__PURE__*/React.createElement("br", null), billing.telephone), !isVirtual && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    style: {
      display: "flex",
      alignItems: "center"
    }
  }, /*#__PURE__*/React.createElement(Checkbox, {
    disabled: true,
    id: "shipToSameAddressPlaceholder",
    value: state.shipToSameAddress,
    name: "shipToSameAddressPlaceholder",
    onChange: () => {}
  }), /*#__PURE__*/React.createElement("label", {
    htmlFor: "shipToSameAddressPlaceholder",
    style: {
      paddingLeft: "0.7em"
    }
  }, t("CHECKOUT.ADDRESS.SHIP_TO_SAME_ADDRESS"))), !state.shipToSameAddress && shipping && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("h3", {
    className: styles.customerFormHeading
  }, shipping.firstname, " ", shipping.lastname), shipping.street && shipping.postcode && shipping.city && /*#__PURE__*/React.createElement("p", null, Object.values(shipping.street).join(", "), /*#__PURE__*/React.createElement("br", null), shipping.postcode, " ", shipping.city, " ", shipping.countryName))))), /*#__PURE__*/React.createElement(Foldable, {
    open: !isAddressValid
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.row
  }, /*#__PURE__*/React.createElement(ContactInfo, {
    StoreInfoContext: StoreInfoContext
  }), /*#__PURE__*/React.createElement(Address, {
    type: "billing",
    hasAddress: !isVirtual,
    StoreInfoContext: StoreInfoContext
  })), !isVirtual && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: styles.row
  }, /*#__PURE__*/React.createElement(CheckboxField, {
    name: "shipToSameAddress",
    checked: state.shipToSameAddress
  }, t("CHECKOUT.ADDRESS.SHIP_TO_SAME_ADDRESS"))), !state.shipToSameAddress && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("h2", {
    className: styles.shipToSameAddressTitle
  }, t("CHECKOUT.ADDRESS.SHIPPING")), /*#__PURE__*/React.createElement("div", {
    className: styles.row
  }, /*#__PURE__*/React.createElement(Address, {
    type: "shipping",
    hasAddress: !isVirtual,
    StoreInfoContext: StoreInfoContext
  })))), showErrors && /*#__PURE__*/React.createElement("div", {
    className: styles.row
  }, /*#__PURE__*/React.createElement("p", {
    className: styles.error
  }, t(`VALIDATION.MISSING_FIELD`))), /*#__PURE__*/React.createElement(Button, {
    className: styles.submitButton,
    type: "submit",
    variant: "primary",
    loading: quoteData.state === "SETTING_ADDRESS"
  }, t("CHECKOUT.CONTINUE"))));
};

export { CustomerForm };
//# sourceMappingURL=index.js.map
